import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../../api/client'
import { CHART_TYPE_LABELS, PLATFORM_LABELS, useMeta } from '../../hooks/useMeta'
import { formatCount, formatDateTime, formatRelative, formatTime } from '../../lib/format'
import { Stat, StatCard } from '../status/StatCard'

interface Freshness {
	charts: number
	disabled: number
	due: number
	overdue: number
	failing: number
	neverPolled: number
	polled1h: number
	polled24h: number
	medianAgeMinutes: number | null
	maxAgeMinutes: number | null
	lastPolledAt: string | null
	lastChangedAt: string | null
}

interface TierDemand {
	tier: string
	description: string
	minMinutes: number
	maxMinutes: number
	charts: number
	pollsPerDay: number
	peakPollsPerDay: number
}

interface PoolDemand {
	pollsPerDay: number
	peakPollsPerDay: number
	budgetPerDay: number
}

interface Polling {
	generatedAt: string
	totals: Freshness
	throughput: {
		lastHour: number
		hourly: { hour: string; polls: number }[]
		queued: number
		oldestDueAt: string | null
		backlogHours: number | null
		fullPassHours: number | null
	}
	priorityStorefronts: string[]
	demand: {
		tiers: TierDemand[]
		rss: PoolDemand
		web: PoolDemand
	}
	priority: {
		id: number
		platform: string
		storefront: string
		chartType: string
		enabled: boolean
		lastPolledAt: string | null
		nextPollAt: string
		lastChangedAt: string | null
	}[]
	storefronts: (Freshness & { code: string; name: string })[]
	genres: (Freshness & { id: number; name: string; parentName: string | null; family: string; topLevel: boolean })[]
}

/** 45m, 5h 10m, 2d 3h */
export function formatAge(minutes: number | null): string {
	if (minutes === null) return '—'
	if (minutes < 60) return `${minutes}m`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`
	return hours % 24 ? `${Math.floor(hours / 24)}d ${hours % 24}h` : `${Math.floor(hours / 24)}d`
}

function formatHours(hours: number | null): string {
	return hours === null ? '—' : formatAge(Math.round(hours * 60))
}

/** Charts are meant to be polled at least every 1–48 hours; a group whose median passes a day is falling behind. */
function ageTone(minutes: number | null) {
	return minutes !== null && minutes > 24 * 60 ? 'down' : undefined
}

type SortKey = 'label' | keyof Freshness
const columns: { key: SortKey; label: string; numeric?: boolean }[] = [
	{ key: 'charts', label: 'Charts', numeric: true },
	{ key: 'due', label: 'Due', numeric: true },
	{ key: 'overdue', label: 'Overdue 1h+', numeric: true },
	{ key: 'failing', label: 'Failing', numeric: true },
	{ key: 'polled24h', label: 'Polled 24h', numeric: true },
	{ key: 'medianAgeMinutes', label: 'Median age', numeric: true },
	{ key: 'maxAgeMinutes', label: 'Oldest', numeric: true },
	{ key: 'lastChangedAt', label: 'Last change' },
]

function FreshnessTable<T extends Freshness>({
	rows,
	labelHeader,
	label,
	rowKey,
	onSelect,
	selected,
}: {
	rows: T[]
	labelHeader: string
	label: (row: T) => ReactNode
	rowKey: (row: T) => string
	onSelect?: (row: T) => void
	selected?: string
}) {
	const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'label', desc: false })
	const sorted =
		sort.key === 'label'
			? rows
			: [...rows].sort((a, b) => compare(a[sort.key as keyof Freshness], b[sort.key as keyof Freshness]))
	if (sort.desc) sorted.reverse()
	const header = (key: SortKey, text: string, numeric?: boolean) => (
		<th
			className={numeric ? 'num' : undefined}
			aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
		>
			<button
				type="button"
				className="sort-button"
				onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== 'label' }))}
			>
				{text}
				{sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : ''}
			</button>
		</th>
	)
	if (rows.length === 0) return <p className="muted">No charts match.</p>
	return (
		<div className="table-scroll">
			<table>
				<thead>
					<tr>
						{header('label', labelHeader)}
						{columns.map((c) => header(c.key, c.label, c.numeric))}
					</tr>
				</thead>
				<tbody>
					{sorted.map((r) => (
						<tr key={rowKey(r)} className={selected === rowKey(r) ? 'selected' : undefined}>
							<td>
								{onSelect ? (
									<button type="button" className="link-button" onClick={() => onSelect(r)}>
										{label(r)}
									</button>
								) : (
									label(r)
								)}
							</td>
							<td className="num">{formatCount(r.charts)}</td>
							<td className="num">{formatCount(r.due)}</td>
							<td className={`num ${r.overdue ? 'down' : ''}`}>{formatCount(r.overdue)}</td>
							<td className={`num ${r.failing ? 'error' : ''}`}>{formatCount(r.failing)}</td>
							<td className="num">{formatCount(r.polled24h)}</td>
							<td className={`num ${ageTone(r.medianAgeMinutes) ?? ''}`}>{formatAge(r.medianAgeMinutes)}</td>
							<td className={`num ${ageTone(r.maxAgeMinutes) ?? ''}`}>
								{formatAge(r.maxAgeMinutes)}
								{r.neverPolled ? <span className="muted small"> · {r.neverPolled} never</span> : null}
							</td>
							<td className="muted small">{formatRelative(r.lastChangedAt)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

/** Nulls sort as the largest value, so "never" lands with the stalest. */
function compare(a: string | number | null, b: string | number | null): number {
	if (a === b) return 0
	if (a === null) return 1
	if (b === null) return -1
	return a < b ? -1 : 1
}

const TIER_LABELS: Record<string, string> = {
	priority: 'Priority',
	major: 'Major',
	standard: 'Standard',
	minor: 'Minor',
	throttled: 'Throttled',
	dormant: 'Dormant',
}

function PoolStat({ label, pool }: { label: string; pool: PoolDemand }) {
	const over = pool.pollsPerDay > pool.budgetPerDay
	return (
		<Stat
			label={`${label}: asked for / allowed per day`}
			value={`${formatCount(pool.pollsPerDay)} / ${formatCount(pool.budgetPerDay)}`}
			tone={over ? 'bad' : 'good'}
		/>
	)
}

/** What the schedule asks for against what the rate limits allow; above the budget, every chart polls later. */
function DemandCard({ demand }: { demand: Polling['demand'] }) {
	return (
		<StatCard title="Demand and budget">
			<PoolStat label="iTunes chart feeds" pool={demand.rss} />
			<PoolStat label="Watch chart pages" pool={demand.web} />
			<p className="muted small">
				Each tier bounds how often its charts are polled; a chart polls sooner within those bounds when its ranking
				changes. Peak is every chart at its tier’s shortest interval. When demand passes the budget, the most overdue
				charts for their cadence go first, so every tier slows in proportion.
			</p>
			<div className="table-scroll">
				<table>
					<thead>
						<tr>
							<th>Tier</th>
							<th>Polled every</th>
							<th className="num">Charts</th>
							<th className="num">Polls/day now</th>
							<th className="num">Peak</th>
						</tr>
					</thead>
					<tbody>
						{demand.tiers.map((t) => (
							<tr key={t.tier}>
								<td title={t.description}>
									{TIER_LABELS[t.tier] ?? t.tier}
									<div className="muted small">{t.description}</div>
								</td>
								<td>
									{t.minMinutes === t.maxMinutes
										? formatAge(t.minMinutes)
										: `${formatAge(t.minMinutes)} – ${formatAge(t.maxMinutes)}`}
								</td>
								<td className="num">{formatCount(t.charts)}</td>
								<td className="num">{formatCount(t.pollsPerDay)}</td>
								<td className="num muted">{formatCount(t.peakPollsPerDay)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</StatCard>
	)
}

export function PollingPanel() {
	const { data: meta } = useMeta()
	const [platform, setPlatform] = useState('')
	const [storefront, setStorefront] = useState('')
	const params = new URLSearchParams()
	if (platform) params.set('platform', platform)
	if (storefront) params.set('storefront', storefront)
	const polling = useQuery({
		queryKey: ['admin-polling', platform, storefront],
		queryFn: () => api.get<Polling>(`/api/admin/polling${params.size ? `?${params}` : ''}`),
		refetchInterval: 60_000,
		placeholderData: (previous) => previous,
	})
	const p = polling.data
	const storefrontName = (code: string) => meta?.storefronts.find((s) => s.code === code)?.name ?? code.toUpperCase()

	return (
		<div className="stack">
			<div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
				<select aria-label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
					<option value="">All platforms</option>
					{meta?.platforms.map((pl) => (
						<option key={pl.id} value={pl.id}>
							{PLATFORM_LABELS[pl.id] ?? pl.name}
						</option>
					))}
				</select>
				<select aria-label="Country" value={storefront} onChange={(e) => setStorefront(e.target.value)}>
					<option value="">All countries</option>
					{meta?.storefronts.map((s) => (
						<option key={s.code} value={s.code}>
							{s.name}
						</option>
					))}
				</select>
				{p && (
					<span className="muted small">
						Updated {formatDateTime(p.generatedAt)} · refreshes every minute
						{polling.isFetching ? ' · loading…' : ''}
					</span>
				)}
			</div>

			{polling.isPending && <div className="page-loading">Loading…</div>}
			{polling.isError && <div className="empty">Polling progress is unavailable: {polling.error.message}</div>}
			{p && (
				<>
					<div className="grid">
						<StatCard title={storefront || platform ? 'Freshness (filtered)' : 'Freshness'}>
							<Stat label="Charts tracked" value={formatCount(p.totals.charts)} />
							<Stat
								label="Polled in the last hour / 24h"
								value={`${formatCount(p.totals.polled1h)} / ${formatCount(p.totals.polled24h)}`}
							/>
							<Stat
								label="Due now / overdue 1h+"
								value={`${formatCount(p.totals.due)} / ${formatCount(p.totals.overdue)}`}
							/>
							<Stat
								label="Median / oldest since polled"
								value={`${formatAge(p.totals.medianAgeMinutes)} / ${formatAge(p.totals.maxAgeMinutes)}`}
								tone={ageTone(p.totals.medianAgeMinutes) ? 'bad' : undefined}
							/>
							<Stat label="Never polled" value={formatCount(p.totals.neverPolled)} />
							<Stat
								label="Last poll failed"
								value={formatCount(p.totals.failing)}
								tone={p.totals.failing ? 'bad' : undefined}
							/>
							<Stat label="Disabled (empty feeds)" value={formatCount(p.totals.disabled)} />
						</StatCard>
						<StatCard title="Throughput (all charts)">
							<Stat label="Polls in the last hour" value={formatCount(p.throughput.lastHour)} />
							<Stat label="Queued polls" value={formatCount(p.throughput.queued)} />
							<Stat label="Oldest queued poll due" value={formatRelative(p.throughput.oldestDueAt)} />
							<Stat label="Charts due now polled in" value={`~${formatHours(p.throughput.backlogHours)}`} />
							<Stat label="Every chart polled once in" value={`~${formatHours(p.throughput.fullPassHours)}`} />
						</StatCard>
					</div>

					<DemandCard demand={p.demand} />

					<StatCard title="Polls per hour">
						<ResponsiveContainer width="100%" height={180}>
							<BarChart data={p.throughput.hourly}>
								<CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
								<XAxis
									dataKey="hour"
									tickFormatter={(h) => formatTime(new Date(h))}
									stroke="var(--text-3)"
									fontSize={12}
									minTickGap={24}
								/>
								<YAxis stroke="var(--text-3)" fontSize={12} width={48} allowDecimals={false} />
								<Tooltip
									labelFormatter={(h) => formatDateTime(String(h))}
									formatter={(v) => [formatCount(Number(v)), 'Polls']}
									contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
								/>
								<Bar dataKey="polls" fill="var(--accent)" radius={[3, 3, 0, 0]} />
							</BarChart>
						</ResponsiveContainer>
					</StatCard>

					{p.priority.length > 0 && (
						<StatCard title={`Hourly top charts (${p.priorityStorefronts.map(storefrontName).join(', ')})`}>
							<div className="table-scroll">
								<table>
									<thead>
										<tr>
											<th>Country</th>
											<th>Platform</th>
											<th>Chart</th>
											<th>Last polled</th>
											<th>Next poll</th>
											<th>Last change</th>
										</tr>
									</thead>
									<tbody>
										{p.priority.map((c) => {
											const late = !c.lastPolledAt || Date.now() - new Date(c.lastPolledAt).getTime() > 60 * 60_000
											return (
												<tr key={c.id}>
													<td>{storefrontName(c.storefront)}</td>
													<td>{PLATFORM_LABELS[c.platform] ?? c.platform}</td>
													<td>
														Top {CHART_TYPE_LABELS[c.chartType] ?? c.chartType}
														{!c.enabled && <span className="muted small"> · disabled</span>}
													</td>
													<td className={late ? 'down' : undefined}>{formatRelative(c.lastPolledAt)}</td>
													<td className="muted small">{formatRelative(c.nextPollAt)}</td>
													<td className="muted small">{formatRelative(c.lastChangedAt)}</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						</StatCard>
					)}

					<StatCard title="By country">
						<p className="muted small" style={{ marginTop: 0 }}>
							Select a country to break its charts down by genre below.
						</p>
						<FreshnessTable
							rows={p.storefronts}
							labelHeader="Country"
							label={(s) => s.name}
							rowKey={(s) => s.code}
							selected={storefront}
							onSelect={(s) => setStorefront(s.code === storefront ? '' : s.code)}
						/>
					</StatCard>

					<StatCard title={storefront ? `By genre in ${storefrontName(storefront)}` : 'By genre'}>
						{storefront && (
							<p className="small" style={{ marginTop: 0 }}>
								<button type="button" className="link-button" onClick={() => setStorefront('')}>
									Show all countries
								</button>
							</p>
						)}
						<FreshnessTable
							rows={p.genres}
							labelHeader="Genre"
							label={(g) => (
								<>
									{g.topLevel ? `All ${g.name}` : g.parentName ? `${g.parentName} › ${g.name}` : g.name}
									{g.family === 'mac' && <span className="muted small"> · Mac</span>}
								</>
							)}
							rowKey={(g) => String(g.id)}
						/>
					</StatCard>
				</>
			)}
		</div>
	)
}
